import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useFetch, useMutate, toMessage } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import { useAuth } from '@/auth/AuthContext'
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  Loading,
  Modal,
  PageHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { dateTime, dateOnly, dateInputToMs, msToDateInput } from '@/lib/format'
import { Money } from '@/lib/currency'

interface Invoice {
  id: string
  clubId: string
  invoiceNumber: string
  purpose: string
  amountDue: number
  status: 'unpaid' | 'paid'
  paymentOrderNumber: string | null
  paidAt: number | null
  amountPaid: number | null
  actFileName: string | null
  actFileMime: string | null
  actUploadedAt: number | null
  periodMonths: number | null
  invoicePdfMime: string | null
  invoiceGeneratedAt: number | null
  emailSentTo: string | null
  emailSentAt: number | null
  createdAt: number
  updatedAt: number
}

interface ClubRate {
  clubId: string
  monthlyRate: number
  updatedAt: number
}

/** Статус онлайн-платежа (GET /v1/payments/:id). */
interface PaymentStatus {
  id: string
  invoiceId: string
  invoiceNumber: string
  status: 'pending' | 'successful' | 'failed' | 'expired'
  amount: number
  isTest: boolean
  errorMessage: string | null
  licenseExpiresAt: number | null
}

/** Сколько раз опрашиваем статус после возврата с платёжной страницы (2 с × 12 ≈ 25 с). */
const PAYMENT_POLL_ATTEMPTS = 12
const PAYMENT_POLL_INTERVAL_MS = 2000

const PERIOD_OPTIONS = [1, 3, 6, 12] as const

interface SettingRow {
  key: string
  value: string
  updatedAt: number
}

/** Ключ настройки: авто-счёт на месяц вместе с напоминанием об окончании подписки. */
const KEY_AUTO_INVOICE = 'financeAutoInvoiceEnabled'

/**
 * Разбор тумблера, хранящегося строкой: отсутствует/пусто = ВКЛ
 * (та же конвенция, что у Telegram-тумблеров на странице «Телеграм-бот»).
 */
function parseFlag(value: string | undefined): boolean {
  if (value === undefined) return true
  const v = value.trim().toLowerCase()
  if (v === '') return true
  return !['0', 'false', 'no', 'off'].includes(v)
}

/**
 * Реквизиты рассылки счетов — обычные клубные настройки (ключи billingEmail и
 * financeAutoInvoiceEnabled в общей таблице settings), без отдельной схемы БД.
 * Редактировать может owner/admin своего клуба и super_admin (с ?clubId).
 */
function BillingEmailCard({
  query
}: {
  query: ReturnType<typeof useConfigQuery>
}): React.JSX.Element {
  const list = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const save = useMutate()
  const toggle = useMutate()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')

  const current = list.data?.find((s) => s.key === 'billingEmail')?.value ?? ''
  const autoInvoice = parseFlag(list.data?.find((s) => s.key === KEY_AUTO_INVOICE)?.value)

  const startEdit = (): void => {
    setValue(current)
    setEditing(true)
  }

  const onSave = async (): Promise<void> => {
    const ok = await save.mutate(() =>
      api.put(`/v1/settings/billingEmail`, { value: value.trim() }, { query })
    )
    if (ok) {
      setEditing(false)
      list.reload()
    }
  }

  const onToggleAutoInvoice = async (next: boolean): Promise<void> => {
    const ok = await toggle.mutate(() =>
      api.put(`/v1/settings/${KEY_AUTO_INVOICE}`, { value: next ? '1' : '0' }, { query })
    )
    if (ok) list.reload()
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '14px 18px',
        marginBottom: 18,
        border: '1px solid var(--hairline-2)',
        borderRadius: 8
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16
        }}
      >
        <div>
          <div className="field__label" style={{ marginBottom: 4 }}>
            Email для счетов
          </div>
          {!editing ? (
            <div className="muted" style={{ fontSize: 14 }}>
              {current || 'не указан — копии PDF не будут отправляться письмом'}
            </div>
          ) : (
            <input
              className="input"
              style={{ minWidth: 280 }}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="buhgalteria@club.by"
              autoFocus
            />
          )}
          {save.error && (
            <div className="form-error" style={{ marginTop: 6 }}>
              {save.error}
            </div>
          )}
        </div>
        {!editing ? (
          <button className="btn btn--ghost btn--sm" onClick={startEdit}>
            {current ? 'Изменить' : 'Указать'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setEditing(false)}
              disabled={save.saving}
            >
              Отмена
            </button>
            <button
              className="btn btn--primary btn--sm"
              onClick={() => void onSave()}
              disabled={save.saving}
            >
              {save.saving && <span className="spin" />}
              Сохранить
            </button>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--hairline-2)', paddingTop: 12 }}>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={autoInvoice}
            disabled={toggle.saving || list.loading}
            onChange={(e) => void onToggleAutoInvoice(e.target.checked)}
          />
          Выставлять счёт на месяц автоматически
        </label>
        <span className="field__hint">
          За 10 дней до окончания подписки счёт формируется сам по ставке клуба и уходит в
          Telegram-чат клуба и на email выше. Если предыдущий счёт ещё не оплачен, новый не
          выставляется — в напоминании придёт его номер.
        </span>
        {toggle.error && (
          <div className="form-error" style={{ marginTop: 6 }}>
            {toggle.error}
          </div>
        )}
      </div>
    </div>
  )
}

export function Finance(): React.JSX.Element {
  const { hasRole } = useAuth()
  const isSuperAdmin = hasRole('super_admin')
  const [tab, setTab] = useState<'invoices' | 'modules'>('invoices')
  const [editingTemplate, setEditingTemplate] = useState(false)
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16
        }}
      >
        <PageHead
          eyebrow="Финансы"
          title={tab === 'invoices' ? 'Счета и акты' : 'Модули'}
          lead={
            tab === 'invoices'
              ? 'Счета клуба от площадки (абонентское обслуживание, регистрация ПК и т.п.), данные оплаты и акты выполненных работ.'
              : 'Каталог платных доп. модулей и какие клубы их используют.'
          }
        />
        {isSuperAdmin && tab === 'invoices' && (
          <button
            className="btn btn--ghost btn--sm"
            style={{ flexShrink: 0, marginBottom: 18 }}
            onClick={() => setEditingTemplate(true)}
          >
            Шаблон счёта
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
          <button
            className={tab === 'invoices' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            onClick={() => setTab('invoices')}
          >
            Счета и акты
          </button>
          <button
            className={tab === 'modules' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            onClick={() => setTab('modules')}
          >
            Модули
          </button>
        </div>
      )}

      {tab === 'invoices' ? (
        <ClubGate>
          <FinanceTable />
        </ClubGate>
      ) : (
        <ModulesAdmin />
      )}
      {editingTemplate && <TemplateForm onClose={() => setEditingTemplate(false)} />}
    </>
  )
}

function statusBadge(inv: Invoice): React.JSX.Element {
  if (inv.status === 'paid') {
    return <span style={{ color: 'var(--green)' }}>Оплачен</span>
  }
  return <span style={{ color: 'var(--amber)' }}>Ожидает оплаты</span>
}

/**
 * Возврат плательщика с платёжной страницы bePaid: `?pay=success|decline|fail|cancel&p=<id>`.
 *
 * Редирект часто прилетает РАНЬШЕ нотификации, поэтому статус платежа опрашиваем
 * (бэкенд на опросе сам переспрашивает bePaid и применяет результат) — до тех пор,
 * пока платёж pending, но не дольше PAYMENT_POLL_ATTEMPTS попыток.
 */
function PaymentReturnBanner({
  query,
  onPaid
}: {
  query: ReturnType<typeof useConfigQuery>
  onPaid: () => void
}): React.JSX.Element | null {
  const [params, setParams] = useSearchParams()
  const result = params.get('pay')
  const paymentId = params.get('p')
  const [status, setStatus] = useState<PaymentStatus | null>(null)
  const [polling, setPolling] = useState(false)

  useEffect(() => {
    if (!result || !paymentId) return
    let active = true
    let attempts = 0
    let timer: number | undefined
    setPolling(true)

    const poll = async (): Promise<void> => {
      attempts += 1
      let done = false
      try {
        const st = await api.get<PaymentStatus>(`/v1/payments/${paymentId}`, { query })
        if (!active) return
        setStatus(st)
        if (st.status === 'successful') {
          done = true
          onPaid()
        } else if (st.status !== 'pending') {
          done = true
        }
      } catch {
        // Разовый сбой сети — не сдаёмся, повторим на следующем тике.
      }
      if (!active) return
      if (done || attempts >= PAYMENT_POLL_ATTEMPTS) {
        setPolling(false)
        return
      }
      timer = window.setTimeout(() => void poll(), PAYMENT_POLL_INTERVAL_MS)
    }

    void poll()
    return () => {
      active = false
      if (timer) window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, paymentId])

  if (!result) return null

  const dismiss = (): void => {
    const next = new URLSearchParams(params)
    next.delete('pay')
    next.delete('p')
    setParams(next, { replace: true })
  }

  const paid = status?.status === 'successful'
  const failed = status?.status === 'failed' || status?.status === 'expired'
  const color = paid
    ? 'var(--green)'
    : failed || result !== 'success'
      ? 'var(--amber)'
      : 'var(--text-2)'

  let text: string
  if (paid) {
    text =
      `Оплата получена${status?.isTest ? ' (тестовый платёж)' : ''}: счёт № ${status?.invoiceNumber} закрыт.` +
      (status?.licenseExpiresAt != null
        ? ` Подписка оплачена до ${dateOnly(status.licenseExpiresAt)} включительно.`
        : '')
  } else if (result === 'cancel') {
    text = 'Оплата отменена — счёт остался неоплаченным.'
  } else if (failed || result === 'decline' || result === 'fail') {
    text = `Платёж не прошёл${status?.errorMessage ? `: ${status.errorMessage}` : ''}. Попробуйте другую карту или оплатите по реквизитам из счёта.`
  } else if (polling) {
    text = 'Проверяем оплату у платёжной системы…'
  } else {
    text =
      'Платёж пока не подтверждён. Если деньги списались, статус обновится автоматически в течение нескольких минут.'
  }

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        marginBottom: 16,
        border: `1px solid ${color}`,
        borderRadius: 8,
        fontSize: 14
      }}
    >
      <span style={{ color, fontSize: 18, lineHeight: 1 }}>{paid ? '✓' : '💳'}</span>
      <span style={{ flex: 1 }}>{text}</span>
      <button type="button" className="btn btn--ghost btn--sm" onClick={dismiss}>
        Скрыть
      </button>
    </div>
  )
}

function FinanceTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<Invoice[]>('/v1/finance/invoices', query, [query?.clubId])
  const rateFetch = useFetch<ClubRate | null>('/v1/finance/rate', query, [query?.clubId])
  const { hasRole } = useAuth()
  const isSuperAdmin = hasRole('super_admin')
  // Самостоятельно формировать счёт может владелец/админ клуба и бухгалтер.
  const canGenerateInvoice = hasRole('owner', 'admin', 'accountant')

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Invoice | null>(null)
  const [paying, setPaying] = useState<Invoice | null>(null)
  const [deleting, setDeleting] = useState<Invoice | null>(null)
  const [uploadingActFor, setUploadingActFor] = useState<Invoice | null>(null)
  const [generating, setGenerating] = useState(false)
  const [editingRate, setEditingRate] = useState(false)

  const del = useMutate()

  // Онлайн-оплата картой (bePaid). Кнопка есть только если эквайринг подключён
  // на бэкенде и у сотрудника есть право платить (viewer — только смотрит).
  const payConfig = useFetch<{ enabled: boolean }>('/v1/payments/config')
  const cardPaymentEnabled = payConfig.data?.enabled === true
  const canPayOnline = hasRole('owner', 'admin', 'accountant', 'super_admin')
  const [payingId, setPayingId] = useState<string | null>(null)
  const [payError, setPayError] = useState<string | null>(null)

  /** Уводим на платёжную страницу bePaid; вернёмся сюда же с ?pay=…&p=… */
  const payByCard = async (inv: Invoice): Promise<void> => {
    setPayError(null)
    setPayingId(inv.id)
    try {
      const res = await api.post<{ redirectUrl: string }>(
        `/v1/finance/invoices/${inv.id}/checkout`,
        undefined,
        { query }
      )
      window.location.assign(res.redirectUrl)
    } catch (err) {
      setPayError(toMessage(err))
      setPayingId(null)
    }
  }

  const rows = list.data ?? []
  const unpaidCount = rows.filter((r) => r.status === 'unpaid').length

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/finance/invoices/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  const downloadFile = async (path: string, fallbackName: string): Promise<void> => {
    try {
      const blob = await api.getBlob(path, { query })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fallbackName
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      // Молча игнорируем — повторная попытка скачивания доступна по той же кнопке.
    }
  }

  const downloadAct = (inv: Invoice): Promise<void> =>
    downloadFile(
      `/v1/finance/invoices/${inv.id}/act`,
      inv.actFileName ?? `${inv.invoiceNumber}.pdf`
    )

  const downloadInvoicePdf = (inv: Invoice): Promise<void> =>
    downloadFile(`/v1/finance/invoices/${inv.id}/pdf`, `${inv.invoiceNumber}.pdf`)

  return (
    <>
      <PaymentReturnBanner query={query} onPaid={list.reload} />

      <BillingEmailCard query={query} />

      {payError && <ErrorState message={payError} />}

      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">
            {rows.length} счёт(ов){unpaidCount > 0 ? `, ${unpaidCount} не оплачено` : ''}
          </span>
          {isSuperAdmin && (
            <span className="muted" style={{ marginLeft: 14 }}>
              Ставка: {rateFetch.data ? <Money value={rateFetch.data.monthlyRate} /> : 'не задана'}
              /мес
            </span>
          )}
        </div>
        <div className="toolbar__right" style={{ display: 'flex', gap: 8 }}>
          {isSuperAdmin && (
            <>
              <button className="btn btn--ghost btn--sm" onClick={() => setEditingRate(true)}>
                Ставка клуба
              </button>
              <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
                {Icon.plus}
                Новый счёт
              </button>
            </>
          )}
          {canGenerateInvoice && (
            <button className="btn btn--primary btn--sm" onClick={() => setGenerating(true)}>
              {Icon.plus}
              Сформировать счёт
            </button>
          )}
        </div>
      </div>

      {list.loading && <Loading />}
      {list.error && <ErrorState message={list.error} />}
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message="Счетов пока нет." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>№ счёта</th>
                <th>Назначение</th>
                <th>Сумма к оплате</th>
                <th>Статус</th>
                <th>№ платёжного поручения</th>
                <th>Дата и время оплаты</th>
                <th>Сумма оплаты</th>
                <th>Акт</th>
                <th>Счёт (PDF)</th>
                {cardPaymentEnabled && canPayOnline && <th>Оплата картой</th>}
                {isSuperAdmin && <th style={{ width: 240 }}>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <code style={{ fontFamily: 'monospace' }}>{inv.invoiceNumber}</code>
                  </td>
                  <td style={{ maxWidth: 260, wordBreak: 'break-word' }}>{inv.purpose}</td>
                  <td>
                    <Money value={inv.amountDue} />
                  </td>
                  <td>{statusBadge(inv)}</td>
                  <td>{inv.paymentOrderNumber ?? <span className="muted">—</span>}</td>
                  <td>{inv.paidAt ? dateTime(inv.paidAt) : <span className="muted">—</span>}</td>
                  <td>
                    {inv.amountPaid != null ? (
                      <Money value={inv.amountPaid} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {inv.actFileName ? (
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => void downloadAct(inv)}
                        title={`Скачать акт: ${inv.actFileName}`}
                      >
                        Скачать акт
                      </button>
                    ) : (
                      <span className="muted">не загружен</span>
                    )}
                  </td>
                  <td>
                    {inv.invoicePdfMime ? (
                      <div>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => void downloadInvoicePdf(inv)}
                          title={`Скачать счёт ${inv.invoiceNumber} в PDF`}
                        >
                          Скачать счёт
                        </button>
                        {inv.emailSentTo && (
                          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                            Отправлен на {inv.emailSentTo}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {cardPaymentEnabled && canPayOnline && (
                    <td>
                      {inv.status === 'unpaid' ? (
                        <button
                          className="btn btn--primary btn--sm"
                          disabled={payingId === inv.id}
                          onClick={() => void payByCard(inv)}
                          title="Оплатить счёт банковской картой (Visa / Mastercard / БЕЛКАРТ)"
                        >
                          {payingId === inv.id ? 'Открываем…' : '💳 Оплатить'}
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  )}
                  {isSuperAdmin && (
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {inv.status === 'unpaid' ? (
                          <>
                            <button
                              className="btn btn--primary btn--sm"
                              onClick={() => setPaying(inv)}
                              title="Внести номер платёжного поручения, дату и сумму — счёт станет оплаченным"
                            >
                              Отметить оплату
                            </button>
                            <IconButton
                              title={`Изменить счёт ${inv.invoiceNumber}`}
                              aria-label="Изменить счёт"
                              onClick={() => setEditing(inv)}
                            >
                              {Icon.edit}
                            </IconButton>
                            <IconButton
                              danger
                              title={`Удалить счёт ${inv.invoiceNumber}`}
                              aria-label="Удалить счёт"
                              onClick={() => setDeleting(inv)}
                            >
                              {Icon.trash}
                            </IconButton>
                          </>
                        ) : null}
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => setUploadingActFor(inv)}
                          title={
                            inv.actFileName
                              ? 'Заменить или удалить загруженный акт'
                              : 'Загрузить скан акта выполненных работ'
                          }
                        >
                          {inv.actFileName ? 'Заменить акт' : 'Загрузить акт'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <InvoiceForm
          mode="create"
          query={query}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            list.reload()
          }}
        />
      )}

      {editing && (
        <InvoiceForm
          mode="edit"
          invoice={editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {editingRate && (
        <RateForm
          query={query}
          currentRate={rateFetch.data ?? null}
          onClose={() => setEditingRate(false)}
          onSaved={() => {
            setEditingRate(false)
            rateFetch.reload()
          }}
        />
      )}

      {generating && (
        <GenerateInvoiceForm
          query={query}
          rate={rateFetch.data ?? null}
          onClose={() => setGenerating(false)}
          onSaved={() => {
            setGenerating(false)
            list.reload()
          }}
        />
      )}

      {paying && (
        <PaymentForm
          invoice={paying}
          query={query}
          onClose={() => setPaying(null)}
          onSaved={() => {
            setPaying(null)
            list.reload()
          }}
        />
      )}

      {uploadingActFor && (
        <ActUploadForm
          invoice={uploadingActFor}
          query={query}
          onClose={() => setUploadingActFor(null)}
          onSaved={() => {
            setUploadingActFor(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить счёт?"
          message={`Счёт ${deleting.invoiceNumber} будет удалён без возможности восстановления.`}
          confirmLabel="Удалить"
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

// ─────────────────────── Создание / редактирование счёта ───────────────────────

function InvoiceForm({
  mode,
  invoice,
  query,
  onClose,
  onSaved
}: {
  mode: 'create' | 'edit'
  invoice?: Invoice
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [purpose, setPurpose] = useState(invoice?.purpose ?? '')
  const [amountDue, setAmountDue] = useState(invoice ? String(invoice.amountDue) : '')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (): Promise<void> => {
    const amount = Number(amountDue.replace(',', '.'))
    if (!purpose.trim()) return setValidation('Укажите назначение платежа.')
    if (!amount || amount <= 0) return setValidation('Сумма должна быть больше нуля.')
    setValidation(null)
    const body = { purpose: purpose.trim(), amountDue: amount }
    const ok = await save.mutate(() =>
      mode === 'create'
        ? api.post('/v1/finance/invoices', body, { query })
        : api.patch(`/v1/finance/invoices/${invoice!.id}`, body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={mode === 'create' ? 'Новый счёт' : `Редактирование счёта ${invoice?.invoiceNumber}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            {mode === 'create' ? 'Создать' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field field--full">
          <label className="field__label">Назначение платежа</label>
          <input
            className="input"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Абонентское обслуживание, июль 2026"
            autoFocus
          />
        </div>
        <div className="field field--full">
          <label className="field__label">Сумма к оплате</label>
          <input
            className="input"
            inputMode="decimal"
            value={amountDue}
            onChange={(e) => setAmountDue(e.target.value)}
            placeholder="21.00"
          />
        </div>
      </div>
      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────── Данные оплаты ───────────────────────

function PaymentForm({
  invoice,
  query,
  onClose,
  onSaved
}: {
  invoice: Invoice
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const now = new Date()
  const [paymentOrderNumber, setPaymentOrderNumber] = useState('')
  const [date, setDate] = useState(msToDateInput(now.getTime()))
  const [time, setTime] = useState(
    `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  )
  const [amountPaid, setAmountPaid] = useState(String(invoice.amountDue))
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (): Promise<void> => {
    if (!paymentOrderNumber.trim()) return setValidation('Укажите номер платёжного поручения.')
    const amount = Number(amountPaid.replace(',', '.'))
    if (!amount || amount <= 0) return setValidation('Сумма оплаты должна быть больше нуля.')
    const [hh, mm] = time.split(':').map(Number)
    const paidAt = dateInputToMs(date) + (hh || 0) * 3_600_000 + (mm || 0) * 60_000
    setValidation(null)
    const ok = await save.mutate(() =>
      api.patch(
        `/v1/finance/invoices/${invoice.id}/payment`,
        { paymentOrderNumber: paymentOrderNumber.trim(), paidAt, amountPaid: amount },
        { query }
      )
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={`Оплата по счёту ${invoice.invoiceNumber}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Сохранить и пометить оплаченным
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field field--full">
          <label className="field__label">Назначение</label>
          <div className="input" style={{ background: 'transparent', cursor: 'default' }}>
            {invoice.purpose} (<Money value={invoice.amountDue} /> к оплате)
          </div>
        </div>
        <div className="field field--full">
          <label className="field__label">№ платёжного поручения</label>
          <input
            className="input"
            value={paymentOrderNumber}
            onChange={(e) => setPaymentOrderNumber(e.target.value)}
            placeholder="ПП-00231"
            autoFocus
          />
        </div>
        <div className="field">
          <label className="field__label">Дата оплаты</label>
          <input
            className="input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label">Время оплаты</label>
          <input
            className="input"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>
        <div className="field field--full">
          <label className="field__label">Сумма оплаты</label>
          <input
            className="input"
            inputMode="decimal"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
          />
          <span className="field__hint">
            Может отличаться от суммы счёта (например, при частичной оплате).
          </span>
        </div>
      </div>
      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────── Загрузка акта ───────────────────────

function ActUploadForm({
  invoice,
  query,
  onClose,
  onSaved
}: {
  invoice: Invoice
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [fileName, setFileName] = useState<string | null>(invoice.actFileName)
  const [fileData, setFileData] = useState<string | null>(null)
  const [mime, setMime] = useState<string>('application/pdf')
  const [validation, setValidation] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const save = useMutate()
  const removeAct = useMutate()

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      setValidation('Допускаются только PDF-файлы.')
      return
    }
    if (file.size > 4_500_000) {
      setValidation('Файл слишком большой (лимит ~4 МБ).')
      return
    }
    setValidation(null)
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // data:application/pdf;base64,XXXX → берём только base64-часть
      const base64 = result.split(',')[1] ?? ''
      setFileData(base64)
      setFileName(file.name)
      setMime(file.type)
    }
    reader.readAsDataURL(file)
  }

  const onSubmit = async (): Promise<void> => {
    if (!fileData || !fileName) return setValidation('Выберите файл акта.')
    const ok = await save.mutate(() =>
      api.post(`/v1/finance/invoices/${invoice.id}/act`, { fileName, mime, fileData }, { query })
    )
    if (ok) onSaved()
  }

  const onRemove = async (): Promise<void> => {
    const ok = await removeAct.mutate(() =>
      api.del(`/v1/finance/invoices/${invoice.id}/act`, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={`Акт по счёту ${invoice.invoiceNumber}`}
      onClose={onClose}
      footer={
        <>
          {invoice.actFileName && (
            <button
              className="btn btn--ghost"
              onClick={() => void onRemove()}
              disabled={removeAct.saving || save.saving}
              style={{ marginRight: 'auto' }}
            >
              {removeAct.saving && <span className="spin" />}
              Удалить акт
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving || !fileData}
          >
            {save.saving && <span className="spin" />}
            Загрузить
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field field--full">
          <label className="field__label">Текущий файл</label>
          <div className="input" style={{ background: 'transparent', cursor: 'default' }}>
            {invoice.actFileName ?? <span className="muted">не загружен</span>}
          </div>
        </div>
        <div className="field field--full">
          <label className="field__label">Новый файл (PDF, до ~4 МБ)</label>
          <input
            ref={inputRef}
            className="input"
            type="file"
            accept="application/pdf"
            onChange={onFileChange}
          />
          {fileName && fileData && <span className="field__hint">Выбран: {fileName}</span>}
        </div>
      </div>
      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
      {removeAct.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {removeAct.error}
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────── Ставка клуба (только super_admin) ───────────────────────

function RateForm({
  query,
  currentRate,
  onClose,
  onSaved
}: {
  query: ReturnType<typeof useConfigQuery>
  currentRate: ClubRate | null
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [rate, setRate] = useState(currentRate ? String(currentRate.monthlyRate) : '')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (): Promise<void> => {
    const value = Number(rate.replace(',', '.'))
    if (!value || value <= 0) return setValidation('Ставка должна быть больше нуля.')
    setValidation(null)
    const ok = await save.mutate(() =>
      api.put('/v1/finance/rate', { monthlyRate: value }, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title="Ставка абонентского обслуживания"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            Сохранить
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field field--full">
          <label className="field__label">Ставка, BYN / месяц</label>
          <input
            className="input"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="21.00"
            autoFocus
          />
          <span className="field__hint">
            По этой ставке владелец/админ клуба сам формирует себе счёт на 1 / 3 / 6 / 12 месяцев.
          </span>
        </div>
      </div>
      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────── Самостоятельное формирование счёта (owner/admin/бухгалтер) ───────────────────────

function GenerateInvoiceForm({
  query,
  rate,
  onClose,
  onSaved
}: {
  query: ReturnType<typeof useConfigQuery>
  rate: ClubRate | null
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [months, setMonths] = useState<1 | 3 | 6 | 12>(1)
  const save = useMutate()

  const preview = rate ? rate.monthlyRate * months : null

  const onSubmit = async (): Promise<void> => {
    const ok = await save.mutate(() =>
      api.post('/v1/finance/invoices/generate', { months }, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title="Сформировать счёт"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving || !rate}
          >
            {save.saving && <span className="spin" />}
            Сформировать и скачать
          </button>
        </>
      }
    >
      {!rate ? (
        <div className="form-error">
          Тариф для вашего клуба пока не настроен платформой — обратитесь к администратору.
        </div>
      ) : (
        <>
          <div className="field field--full" style={{ marginBottom: 16 }}>
            <label className="field__label">Период оплаты</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PERIOD_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={m === months ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                  onClick={() => setMonths(m)}
                >
                  {m} {m === 1 ? 'месяц' : 'мес.'}
                </button>
              ))}
            </div>
          </div>
          <div className="field field--full">
            <label className="field__label">Сумма к оплате</label>
            <div
              className="input"
              style={{ background: 'transparent', cursor: 'default', fontSize: 18 }}
            >
              <Money value={preview ?? 0} />
            </div>
            <span className="field__hint">
              {rate.monthlyRate.toFixed(2)} BYN/мес × {months} мес. После формирования счёт можно
              будет скачать в PDF — копия также отправится на email из реквизитов клуба, если он
              указан.
            </span>
          </div>
        </>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────── Шаблон счёта (только super_admin) ───────────────────────

interface InvoiceTemplate {
  companyName: string
  companyUnp: string | null
  companyAddress: string | null
  companyBank: string | null
  companyPhone: string | null
  companyEmail: string | null
  companyCity: string | null
  footerNote: string | null
}

function TemplateForm({ onClose }: { onClose: () => void }): React.JSX.Element {
  const tpl = useFetch<InvoiceTemplate>('/v1/finance/template', undefined, [])
  const save = useMutate()

  const [form, setForm] = useState<InvoiceTemplate | null>(null)
  const data = form ?? tpl.data ?? null

  const set = (patch: Partial<InvoiceTemplate>): void => {
    if (!data) return
    setForm({ ...data, ...patch })
  }

  const onSubmit = async (): Promise<void> => {
    if (!data) return
    const ok = await save.mutate(() => api.put('/v1/finance/template', data))
    if (ok) onClose()
  }

  return (
    <Modal
      title="Шаблон счёта"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving || !data}
          >
            {save.saving && <span className="spin" />}
            Сохранить
          </button>
        </>
      }
    >
      {tpl.loading && <Loading />}
      {tpl.error && <ErrorState message={tpl.error} />}
      {data && (
        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label">Название компании (в шапке счёта)</label>
            <input
              className="input"
              value={data.companyName}
              onChange={(e) => set({ companyName: e.target.value })}
              autoFocus
            />
            <span className="field__hint">
              В бланке печатается блоком «Оператор». Сокращение формы («ООО «Ромашка»») раскрывается
              автоматически: «Общество с ограниченной ответственностью «Ромашка»».
            </span>
          </div>
          <div className="field">
            <label className="field__label">Город составления</label>
            <input
              className="input"
              value={data.companyCity ?? ''}
              onChange={(e) => set({ companyCity: e.target.value })}
              placeholder="Речица"
            />
            <span className="field__hint">
              Печатается в шапке слева от даты. Пусто — берётся из адреса.
            </span>
          </div>
          <div className="field">
            <label className="field__label">УНП</label>
            <input
              className="input"
              value={data.companyUnp ?? ''}
              onChange={(e) => set({ companyUnp: e.target.value })}
            />
          </div>
          <div className="field">
            <label className="field__label">Телефон</label>
            <input
              className="input"
              value={data.companyPhone ?? ''}
              onChange={(e) => set({ companyPhone: e.target.value })}
            />
          </div>
          <div className="field field--full">
            <label className="field__label">Адрес</label>
            <input
              className="input"
              value={data.companyAddress ?? ''}
              onChange={(e) => set({ companyAddress: e.target.value })}
            />
          </div>
          <div className="field field--full">
            <label className="field__label">Банковские реквизиты</label>
            <input
              className="input"
              value={data.companyBank ?? ''}
              onChange={(e) => set({ companyBank: e.target.value })}
              placeholder="р/с ..., в ОАО «...»"
            />
          </div>
          <div className="field field--full">
            <label className="field__label">Email компании (для связи в счёте)</label>
            <input
              className="input"
              value={data.companyEmail ?? ''}
              onChange={(e) => set({ companyEmail: e.target.value })}
            />
          </div>
          <div className="field field--full">
            <label className="field__label">Текст в подвале счёта</label>
            <textarea
              className="input"
              rows={3}
              value={data.footerNote ?? ''}
              onChange={(e) => set({ footerNote: e.target.value })}
              placeholder="Счёт действителен 5 банковских дней..."
            />
          </div>
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}

// ─────────────────────── Модули: каталог + матрица клубов (super_admin) ───────────────────────

interface FinanceModule {
  id: string
  name: string
  monthlyPrice: number
}

interface ModuleMatrixRow {
  clubId: string
  clubName: string
  modules: Array<{ moduleId: string; enabled: boolean }>
  total: number
}

function ModulesAdmin(): React.JSX.Element {
  const catalog = useFetch<FinanceModule[]>('/v1/modules', undefined, [])
  const matrix = useFetch<ModuleMatrixRow[]>('/v1/modules/matrix', undefined, [])
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<FinanceModule | null>(null)
  const [deleting, setDeleting] = useState<FinanceModule | null>(null)
  const del = useMutate()

  const reloadAll = (): void => {
    catalog.reload()
    matrix.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/modules/${deleting.id}`))
    if (ok) {
      setDeleting(null)
      reloadAll()
    }
  }

  const modules = catalog.data ?? []

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 18 }}>
        <div className="toolbar__left">
          <span className="muted">{modules.length} модул(ей) в каталоге</span>
        </div>
        <div className="toolbar__right">
          <button className="btn btn--primary btn--sm" onClick={() => setAdding(true)}>
            {Icon.plus}
            Новый модуль
          </button>
        </div>
      </div>

      {catalog.loading && <Loading />}
      {catalog.error && <ErrorState message={catalog.error} />}
      {!catalog.loading && modules.length === 0 && (
        <EmptyState message="Каталог модулей пуст — добавьте первый модуль." />
      )}

      {modules.length > 0 && (
        <div className="table-wrap" style={{ marginBottom: 30 }}>
          <table className="rtable">
            <thead>
              <tr>
                <th>Модуль</th>
                <th>Цена, BYN/мес</th>
                <th style={{ width: 96 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>
                    <Money value={m.monthlyPrice} />
                  </td>
                  <td>
                    <div className="row-actions">
                      <IconButton
                        title={`Изменить модуль «${m.name}»`}
                        aria-label="Изменить модуль"
                        onClick={() => setEditing(m)}
                      >
                        {Icon.edit}
                      </IconButton>
                      <IconButton
                        danger
                        title={`Удалить модуль «${m.name}»`}
                        aria-label="Удалить модуль"
                        onClick={() => setDeleting(m)}
                      >
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ marginBottom: 12 }}>Какие клубы используют модули</h3>
      {matrix.loading && <Loading />}
      {matrix.error && <ErrorState message={matrix.error} />}
      {!matrix.loading && (matrix.data ?? []).length === 0 && (
        <EmptyState message="Клубов пока нет." />
      )}
      {modules.length > 0 && (matrix.data ?? []).length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Клуб</th>
                {modules.map((m) => (
                  <th key={m.id} style={{ textAlign: 'center' }}>
                    {m.name}
                  </th>
                ))}
                <th>Итого модули/мес</th>
              </tr>
            </thead>
            <tbody>
              {(matrix.data ?? []).map((row) => (
                <tr key={row.clubId}>
                  <td>{row.clubName}</td>
                  {modules.map((m) => {
                    const enabled = row.modules.find((rm) => rm.moduleId === m.id)?.enabled ?? false
                    return (
                      <td key={m.id} style={{ textAlign: 'center' }}>
                        {enabled ? (
                          <span style={{ color: 'var(--green)' }}>✓</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )
                  })}
                  <td>
                    <Money value={row.total} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(adding || editing) && (
        <ModuleForm
          module={editing}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
          onSaved={() => {
            setAdding(false)
            setEditing(null)
            reloadAll()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить модуль?"
          message={`Модуль «${deleting.name}» будет удалён у всех клубов, где он был включён.`}
          confirmLabel="Удалить"
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function ModuleForm({
  module: editingModule,
  onClose,
  onSaved
}: {
  module: FinanceModule | null
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [name, setName] = useState(editingModule?.name ?? '')
  const [price, setPrice] = useState(editingModule ? String(editingModule.monthlyPrice) : '')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (): Promise<void> => {
    if (!name.trim()) return setValidation('Укажите название модуля.')
    const value = Number(price.replace(',', '.'))
    if (!value || value < 0) return setValidation('Цена должна быть числом ≥ 0.')
    setValidation(null)
    const body = { name: name.trim(), monthlyPrice: value }
    const ok = await save.mutate(() =>
      editingModule
        ? api.patch(`/v1/modules/${editingModule.id}`, body)
        : api.post('/v1/modules', body)
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={editingModule ? `Редактирование модуля «${editingModule.name}»` : 'Новый модуль'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={save.saving}>
            Отмена
          </button>
          <button
            className="btn btn--primary"
            onClick={() => void onSubmit()}
            disabled={save.saving}
          >
            {save.saving && <span className="spin" />}
            {editingModule ? 'Сохранить' : 'Создать'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field field--full">
          <label className="field__label">Название модуля</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Бронирования"
            autoFocus
          />
        </div>
        <div className="field field--full">
          <label className="field__label">Цена, BYN/мес</label>
          <input
            className="input"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="8.00"
          />
        </div>
      </div>
      {validation && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {validation}
        </div>
      )}
      {save.error && (
        <div className="form-error" style={{ marginTop: 14 }}>
          {save.error}
        </div>
      )}
    </Modal>
  )
}
